/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssCheckboxComponent } from '../checkbox/checkbox.component';

export interface CheckboxGroupOption<T = unknown> {
  label: string;
  value: T;
  disabled?: boolean;
  id?: string;
}

@Component({
  selector: 'umss-checkbox-group',
  standalone: true,
  imports: [CommonModule, UmssCheckboxComponent],
  templateUrl: './checkbox-group.component.html',
  styleUrls: ['./checkbox-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssCheckboxGroupComponent),
      multi: true
    }
  ]
})
export class UmssCheckboxGroupComponent<
  T = unknown
> implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  private _generatedId = `umss-checkbox-group-${Math.random().toString(36).substr(2, 9)}`;

  constructor() {}

  /** Content props */
  @Input() label?: string; // Group label
  @Input() options: CheckboxGroupOption<T>[] = []; // Array of checkbox options
  @Input() name?: string; // Form name attribute

  /** Variant props - Well-typed and comprehensive */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular'; // Size variant
  @Input() variant: 'Default' | 'Card' = 'Default'; // Visual variant for the group container
  @Input() layout: 'Vertical' | 'Horizontal' | 'Grid' = 'Vertical'; // Group layout variant

  /** State props */
  @Input() selectedValues: T[] = []; // Array of selected values
  @Input() disabled: boolean = false; // Disabled state for entire group
  @Input() required: boolean = false; // Required field indicator
  @Input() error: boolean = false; // Error state
  @Input() maxSelections?: number; // Maximum number of selections allowed
  @Input() minSelections?: number; // Minimum number of selections required
  @Input() showSelectAll: boolean = true; // Whether to show the "Select All" checkbox

  /** Event outputs */
  @Output() selectedValuesChange = new EventEmitter<T[]>();
  @Output() selectionChange = new EventEmitter<{
    value: T;
    checked: boolean;
    selectedValues: T[];
  }>();

  /** ControlValueAccessor implementation */
  private onChange = (value: T[]) => {};
  private onTouched = () => {};

  writeValue(value: T[]): void {
    this.selectedValues = value || [];
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onCheckboxChange(option: CheckboxGroupOption<T>, checked: boolean): void {
    if (this.disabled || option.disabled) return;

    let newSelectedValues = [...this.selectedValues];

    if (checked) {
      // Check if we can add more selections
      if (
        this.maxSelections &&
        newSelectedValues.length >= this.maxSelections
      ) {
        return; // Don't allow more selections
      }

      // Add to selection if not already selected
      if (!newSelectedValues.includes(option.value)) {
        newSelectedValues.push(option.value);
      }
    } else {
      // Remove from selection
      newSelectedValues = newSelectedValues.filter(
        (val) => val !== option.value
      );
    }

    this.selectedValues = newSelectedValues;

    // Emit events
    this.selectedValuesChange.emit(this.selectedValues);
    this.selectionChange.emit({
      value: option.value,
      checked,
      selectedValues: this.selectedValues
    });

    // ControlValueAccessor
    this.onChange(this.selectedValues);
    this.onTouched();
  }

  /** Helper methods */
  isSelected(value: T): boolean {
    return this.selectedValues.includes(value);
  }

  isIndeterminate(): boolean {
    return (
      this.selectedValues.length > 0 &&
      this.selectedValues.length < this.options.length
    );
  }

  isAllSelected(): boolean {
    const selectableOptions = this.options.filter((opt) => !opt.disabled);
    return (
      selectableOptions.length > 0 &&
      selectableOptions.every((opt) =>
        this.selectedValues.includes(opt.value as T)
      )
    );
  }

  selectAll(): void {
    if (this.disabled) return;

    const selectableOptions = this.options.filter((opt) => !opt.disabled);
    const allValues = selectableOptions.map((opt) => opt.value as T);

    // Check max selections constraint
    if (this.maxSelections && allValues.length > this.maxSelections) {
      return;
    }

    this.selectedValues = allValues;
    this.selectedValuesChange.emit(this.selectedValues);
    this.onChange(this.selectedValues);
    this.onTouched();
  }

  deselectAll(): void {
    if (this.disabled) return;

    this.selectedValues = [];
    this.selectedValuesChange.emit(this.selectedValues);
    this.onChange(this.selectedValues);
    this.onTouched();
  }

  toggleAll(): void {
    if (this.isAllSelected()) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  /** Computed properties */
  get hasError(): boolean {
    if (this.error) return true;

    // Check min selections constraint
    if (this.minSelections && this.selectedValues.length < this.minSelections) {
      return true;
    }

    return false;
  }

  get computedId(): string {
    return this._generatedId;
  }

  /** TrackBy function for performance */
  trackByValue(index: number, option: CheckboxGroupOption): unknown {
    return option.value;
  }
}
